import { useForm, usePage, router } from '@inertiajs/react'
import { useState } from 'react'
import Heading from '@arianna/design-system/Heading'
import Text from '@arianna/design-system/Text'
import Button from '@arianna/design-system/Button'
import Card from '@arianna/design-system/Card'
import Input from '@arianna/design-system/Input'
import Badge from '@arianna/design-system/Badge'
import AdminLayout from '../../components/AdminLayout'

interface Release {
  id: number
  revision: number
  storage_key: string
  released_at: string | null
  ingest_warnings: Array<{
    sub_para_id: string
    prev_block_count: number
    new_block_count: number
    highlights_at_risk: number
  }> | null
}

interface Version {
  id: number
  version_number: number
  subtitle: string | null
  published_at: string | null
  current_release: Release | null
}

interface Manual {
  id: number
  slug: string
  title: string
  author: string
  versions: Version[]
}

interface SharedProps extends Record<string, unknown> {
  flash?: {
    alert?: string
    notice?: string
  }
}

interface Props {
  manuals: Manual[]
}

// ── Styled file input (hides the native "Choose file" button behind a DS-styled label) ──

function StyledFileInput({
  id,
  accept,
  file,
  onChange,
  buttonLabel,
}: {
  id: string
  accept: string
  file: File | null
  onChange: (file: File | null) => void
  buttonLabel: string
}) {
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <label
        htmlFor={id}
        className="inline-flex items-center gap-2 cursor-pointer border border-line rounded-md px-3 py-2 text-sm bg-surface hover:bg-surface-2 text-ink"
      >
        {buttonLabel}
      </label>
      <input
        id={id}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => onChange(e.target.files?.[0] ?? null)}
      />
      <Text variant="meta" className="text-ink-3 font-mono truncate max-w-[220px]">
        {file ? file.name : 'Nessun file selezionato'}
      </Text>
    </div>
  )
}

function IngestWarnings({ warnings }: { warnings: Release['ingest_warnings'] }) {
  if (!warnings || warnings.length === 0) return null

  const totalAtRisk = warnings.reduce((sum, w) => sum + w.highlights_at_risk, 0)

  return (
    <div
      className="mt-3 rounded-md p-3 border border-gold"
      style={{ background: 'color-mix(in oklab, var(--gold) 12%, transparent)' }}
    >
      <Text variant="meta" className="font-semibold text-ink-2 block mb-2">
        Avviso anti-orfani
      </Text>
      <Text variant="meta" className="text-ink-2 block text-[13px]">
        Questa release cambia il numero di blocchi in {warnings.length} sotto-paragrafo/i;{' '}
        {totalAtRisk} highlight potrebbero diventare orfani.
      </Text>
      <div className="mt-2 pl-4">
        {warnings.map((w) => (
          <Text
            key={w.sub_para_id}
            variant="meta"
            as="div"
            className="text-ink-3 text-xs font-mono"
          >
            {w.sub_para_id}: {w.prev_block_count} → {w.new_block_count} blocchi (
            {w.highlights_at_risk} highlight a rischio)
          </Text>
        ))}
      </div>
    </div>
  )
}

// ── Types for delete confirmation ────────────────────────────────────────────

type DeleteTarget =
  | { kind: 'manual'; id: number; title: string }
  | { kind: 'version'; id: number; versionNumber: number; manualTitle: string }
  | { kind: 'release'; id: number; revision: number; versionId: number }

// ── Confirm dialog (in-app, never window.confirm) ────────────────────────────

function ConfirmDeleteDialog({ target, onCancel }: { target: DeleteTarget; onCancel: () => void }) {
  function handleConfirm() {
    if (target.kind === 'manual') {
      router.delete(`/admin/manuals/${target.id}`, { preserveScroll: true })
    } else if (target.kind === 'version') {
      router.delete(`/admin/manual_versions/${target.id}`, { preserveScroll: true })
    } else {
      router.delete(`/admin/manual_versions/${target.versionId}/releases/${target.id}`, {
        preserveScroll: true,
      })
    }
    onCancel()
  }

  let title: string
  let message: string

  if (target.kind === 'manual') {
    title = `Eliminare «${target.title}»?`
    message =
      'Verranno rimossi tutte le versioni, le release e i dati degli studenti (accessi, progressi, annotazioni, quiz). Azione irreversibile.'
  } else if (target.kind === 'version') {
    title = `Eliminare v${target.versionNumber} di «${target.manualTitle}»?`
    message =
      'Verranno rimossi tutte le release della versione e i dati degli studenti (progressi, annotazioni, quiz). Azione irreversibile.'
  } else {
    title = `Eliminare release r${target.revision}?`
    message =
      'La release verrà rimossa definitivamente. Se era la release corrente, la revisione precedente diventerà quella attiva. Azione irreversibile.'
  }

  return (
    /* Backdrop */
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/45"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel()
      }}
    >
      {/* Panel */}
      <div className="bg-bg border border-line rounded-lg p-6 max-w-[440px] w-[90%] shadow-2xl">
        <Heading id="confirm-dialog-title" variant="h3" className="mb-3">
          {title}
        </Heading>
        <Text variant="body" className="text-ink-2 mb-5 block">
          {message}
        </Text>
        <div className="flex gap-3 justify-end">
          <Button type="button" variant="ghost" size="md" onClick={onCancel}>
            Annulla
          </Button>
          <Button
            type="button"
            variant="accent"
            size="md"
            style={{ background: 'var(--red)', borderColor: 'var(--red)' }}
            onClick={handleConfirm}
          >
            Elimina
          </Button>
        </div>
      </div>
    </div>
  )
}

function UploadReleaseForm({ versionId }: { versionId: number }) {
  const form = useForm<{ file: File | null; pdf: File | null }>({ file: null, pdf: null })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.data.file) return
    form.post(`/admin/manual_versions/${versionId}/releases`, {
      forceFormData: true,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="mt-3 flex gap-3 items-center flex-wrap">
      <StyledFileInput
        id={`release-file-${versionId}`}
        accept=".arianna"
        file={form.data.file}
        onChange={(file) => form.setData('file', file)}
        buttonLabel="Scegli file .arianna"
      />
      <StyledFileInput
        id={`release-pdf-${versionId}`}
        accept=".pdf"
        file={form.data.pdf}
        onChange={(file) => form.setData('pdf', file)}
        buttonLabel="PDF (opzionale)"
      />
      <Button type="submit" variant="ghost" size="sm" disabled={!form.data.file || form.processing}>
        {form.processing ? 'Caricamento…' : 'Carica release'}
      </Button>
      {form.errors.file && (
        <Text variant="meta" className="text-red text-xs">
          {form.errors.file}
        </Text>
      )}
    </form>
  )
}

function CreateVersionForm({ manualId }: { manualId: number }) {
  const form = useForm({ version_number: '', subtitle: '' })
  const [open, setOpen] = useState(false)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    form.post(`/admin/manuals/${manualId}/versions`, {
      onSuccess: () => {
        setOpen(false)
        form.reset()
      },
    })
  }

  if (!open) {
    return (
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
        + Nuova versione
      </Button>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="flex gap-3 items-center mt-3 flex-wrap">
      <Input
        type="number"
        placeholder="Numero versione"
        value={form.data.version_number}
        onChange={(e) => form.setData('version_number', e.target.value)}
        className="w-[140px]"
        required
      />
      <Input
        type="text"
        placeholder="Sottotitolo (opzionale)"
        value={form.data.subtitle}
        onChange={(e) => form.setData('subtitle', e.target.value)}
        className="w-[220px]"
      />
      <Button type="submit" variant="primary" size="sm" disabled={form.processing}>
        Crea
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
        Annulla
      </Button>
    </form>
  )
}

function CreateManualTrigger({ onClick }: { onClick: () => void }) {
  return (
    <Button variant="accent" size="md" onClick={onClick}>
      + Nuovo manuale
    </Button>
  )
}

function CreateManualFormPanel({ onClose }: { onClose: () => void }) {
  const form = useForm<{ bundle: File | null; pdf: File | null }>({ bundle: null, pdf: null })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.data.bundle || form.processing) return
    form.post('/admin/manuals', {
      forceFormData: true,
      onSuccess: () => {
        onClose()
        form.reset()
      },
    })
  }

  return (
    <Card padding="md" className="mb-6">
      <Heading variant="h3" className="mb-4">
        Nuovo manuale
      </Heading>
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <div>
          <Text variant="meta" className="block mb-1 text-ink-2 font-medium">
            Bundle .arianna
          </Text>
          <StyledFileInput
            id="new-manual-bundle"
            accept=".arianna"
            file={form.data.bundle}
            onChange={(file) => form.setData('bundle', file)}
            buttonLabel="Scegli file .arianna"
          />
          <Text variant="meta" className="block mt-1 text-ink-4 text-xs">
            Slug, titolo, autore e sottotitolo vengono letti dal bundle.
          </Text>
        </div>
        <div>
          <Text variant="meta" className="block mb-1 text-ink-2 font-medium">
            PDF (opzionale)
          </Text>
          <StyledFileInput
            id="new-manual-pdf"
            accept=".pdf"
            file={form.data.pdf}
            onChange={(file) => form.setData('pdf', file)}
            buttonLabel="Scegli PDF"
          />
        </div>
        <div className="flex gap-3 mt-2">
          <Button
            type="submit"
            variant="primary"
            size="md"
            disabled={!form.data.bundle || form.processing}
          >
            {form.processing ? 'Caricamento…' : 'Carica'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="md"
            onClick={() => {
              onClose()
              form.reset()
            }}
          >
            Annulla
          </Button>
        </div>
        {Object.values(form.errors).map((err, i) => (
          <Text key={i} variant="meta" className="text-red text-xs">
            {err}
          </Text>
        ))}
      </form>
    </Card>
  )
}

export default function AdminManuals({ manuals }: Props) {
  const { flash } = usePage<SharedProps>().props
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null)
  const [createOpen, setCreateOpen] = useState(false)

  return (
    <AdminLayout
      title="Manuali"
      active="manuals"
      actions={<CreateManualTrigger onClick={() => setCreateOpen(true)} />}
    >
      {flash?.notice && (
        <div className="mb-4 rounded-sm px-4 py-3 border border-green">
          <Text variant="meta" className="text-green font-medium">
            {flash.notice}
          </Text>
        </div>
      )}

      {flash?.alert && (
        <div className="mb-4 rounded-sm px-4 py-3 border border-red">
          <Text variant="meta" className="text-red font-medium">
            {flash.alert}
          </Text>
        </div>
      )}

      {createOpen && <CreateManualFormPanel onClose={() => setCreateOpen(false)} />}

      {manuals.length === 0 && (
        <Text variant="body" className="text-ink-3">
          Nessun manuale ancora creato.
        </Text>
      )}

      <div className="flex flex-col gap-4">
        {manuals.map((manual) => (
          <Card key={manual.id} padding="lg" className="hover:border-line-2 transition-colors">
            <div className="flex items-start justify-between mb-3 gap-3">
              <div>
                <Heading variant="cardLg" className="mb-1">
                  {manual.title}
                </Heading>
                <div className="flex items-center gap-2">
                  <Text variant="meta" className="text-ink-3 text-[13px]">
                    {manual.author}
                  </Text>
                  <span className="font-mono text-xs text-ink-3 bg-surface-2 border border-line rounded px-1.5 py-0.5">
                    {manual.slug}
                  </span>
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-ink-4 hover:text-red shrink-0"
                onClick={() =>
                  setDeleteTarget({ kind: 'manual', id: manual.id, title: manual.title })
                }
              >
                Elimina
              </Button>
            </div>

            <CreateVersionForm manualId={manual.id} />

            {manual.versions.length > 0 && (
              <div className="mt-4 border-l border-line pl-4 ml-1 flex flex-col gap-3">
                {manual.versions.map((v) => (
                  <div key={v.id}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-accent bg-surface-2 border border-line rounded px-1.5 py-0.5">
                          v{v.version_number}
                        </span>
                        {v.subtitle && (
                          <Text variant="meta" className="text-ink-3">
                            {v.subtitle}
                          </Text>
                        )}
                        {v.current_release?.released_at ? (
                          <Badge variant="green">Pubblicato</Badge>
                        ) : v.current_release ? (
                          <Badge variant="neutral">Bozza</Badge>
                        ) : null}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-ink-4 hover:text-red shrink-0"
                        onClick={() =>
                          setDeleteTarget({
                            kind: 'version',
                            id: v.id,
                            versionNumber: v.version_number,
                            manualTitle: manual.title,
                          })
                        }
                      >
                        Elimina
                      </Button>
                    </div>
                    {v.current_release ? (
                      <div className="mt-1">
                        <div className="flex items-center gap-3">
                          <span className="font-mono text-xs text-accent bg-surface-2 border border-line rounded px-1.5 py-0.5">
                            r{v.current_release.revision}
                          </span>
                          {v.current_release.released_at && (
                            <Text variant="meta" className="text-ink-3 font-mono text-xs">
                              {new Date(v.current_release.released_at).toLocaleDateString('it-IT')}
                            </Text>
                          )}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-ink-4 hover:text-red h-auto px-1.5 py-0.5 text-[11px]"
                            onClick={() =>
                              setDeleteTarget({
                                kind: 'release',
                                id: v.current_release!.id,
                                revision: v.current_release!.revision,
                                versionId: v.id,
                              })
                            }
                          >
                            Elimina
                          </Button>
                        </div>
                        <IngestWarnings warnings={v.current_release.ingest_warnings} />
                      </div>
                    ) : (
                      <Text variant="meta" className="text-ink-4 text-xs">
                        Nessuna release ancora caricata
                      </Text>
                    )}
                    <UploadReleaseForm versionId={v.id} />
                  </div>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>

      {deleteTarget && (
        <ConfirmDeleteDialog target={deleteTarget} onCancel={() => setDeleteTarget(null)} />
      )}
    </AdminLayout>
  )
}
